import { ChevronDown, Eye, Palette, RefreshCw, SlidersHorizontal, Sparkles } from 'lucide-react'
import DeviceScreenPreview from '../../../../components/DeviceScreenPreview'
import { emotionLabelEntry } from '../../../../utils/emotion'

const THEMES = [
  { id: 'robo', zh: 'Robo 真实', en: 'Robo Live', titleZh: '固件默认 RoboEyes', titleEn: 'Firmware default RoboEyes' },
  { id: 'chamii', zh: 'Chamii 可爱', en: 'Chamii Cute', titleZh: 'Chamii 可爱眼睛（仅预览）', titleEn: 'Chamii cute eyes (preview only)' },
  { id: 'miaoban', zh: '喵伴 EAF', en: 'Miaoban EAF', titleZh: '喵伴 EAF 动画（仅预览）', titleEn: 'Miaoban EAF animation (preview only)' },
  { id: 'raw', zh: 'Raw 真实', en: 'Raw Live', titleZh: 'RawEyes 调试预览', titleEn: 'RawEyes tuning preview' },
  { id: 'oled', zh: 'OLED', en: 'OLED' },
  { id: 'classic', zh: '经典', en: 'Classic' },
  { id: 'ani', zh: 'Ani', en: 'Ani' },
]

const EYE_COLORS = [
  { name: 'Orange', color: '#ff9600' },
  { name: 'Cyan', color: '#06b6d4' },
  { name: 'Green', color: '#22c55e' },
  { name: 'Purple', color: '#a855f7' },
  { name: 'Pink', color: '#fb7185' },
  { name: 'White', color: '#ffffff' },
]

const EMOTION_PRESETS = [
  { emotion: 'neutral', emoji: '😶' },
  { emotion: 'happy', emoji: '🙂' },
  { emotion: 'laughing', emoji: '😄' },
  { emotion: 'loving', emoji: '😍' },
  { emotion: 'embarrassed', emoji: '😳' },
  { emotion: 'cool', emoji: '😎' },
  { emotion: 'winking', emoji: '😉' },
  { emotion: 'thinking', emoji: '🤔' },
  { emotion: 'confused', emoji: '😕' },
  { emotion: 'surprised', emoji: '😮' },
  { emotion: 'angry', emoji: '😠' },
  { emotion: 'sad', emoji: '😢' },
  { emotion: 'sleepy', emoji: '😴' },
  { emotion: 'relaxed', emoji: '😌' },
  { emotion: 'silly', emoji: '😜' },
  { emotion: 'confident', emoji: '😏' },
  { emotion: 'delicious', emoji: '😋' },
]

export function MonitorPreviewPanel({
  controlReady,
  deviceId,
  emotion,
  emotionEmoji,
  eyeColor,
  eyeTheme,
  isPublicTest,
  lastSentEyeColorRef,
  previewPoke,
  sendEmotion,
  sendEyeColor,
  setEyeColor,
  setEyeTheme,
  setPreviewPoke,
  setShowPreviewTools,
  showPreviewTools,
  tx,
}) {
  const toolsVisible = !isPublicTest || showPreviewTools

  return (
    <aside className="min-w-0 overflow-hidden rounded-[28px] border border-slate-200/80 bg-white shadow-[0_22px_70px_-40px_rgba(15,23,42,0.45)] dark:border-slate-800 dark:bg-slate-950">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200/70 px-4 py-4 sm:px-5 dark:border-slate-800">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-amber-100 text-amber-800 dark:bg-amber-400/10 dark:text-amber-200">
            <Eye className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0">
            <h2 className="font-black text-slate-950 dark:text-white">{tx({ zh: '设备视觉实验室', en: 'Device visual lab' })}</h2>
            <p className="truncate text-xs font-semibold text-slate-500 dark:text-slate-400">{deviceId || tx({ zh: '尚未选择设备', en: 'No device selected' })}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setPreviewPoke((value) => (value + 1) % 1000000)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-300/40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-900"
          title={tx({ zh: '触发一次眨眼/扫视', en: 'Trigger a blink/look' })}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {tx({ zh: '动作自检', en: 'Motion check' })}
        </button>
      </header>

      <div className="space-y-4 p-4 sm:p-5">
        <div className="relative overflow-hidden rounded-[24px] bg-slate-950 p-4 shadow-inner ring-1 ring-white/5">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.22),transparent_46%),linear-gradient(135deg,rgba(255,255,255,0.04),transparent_45%)]" />
          <div className="relative">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
                <span className={`h-2 w-2 rounded-full ${controlReady ? 'bg-emerald-400' : 'bg-amber-300'}`} />
                {controlReady ? tx({ zh: '设备同步预览', en: 'Device-synced preview' }) : tx({ zh: '本地预览模式', en: 'Local preview mode' })}
              </div>
              <div className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-bold text-white">
                {emotionEmoji} {tx(emotionLabelEntry(emotion))}
              </div>
            </div>
            <DeviceScreenPreview theme={eyeTheme} emotion={emotion} poke={previewPoke} accent={eyeColor} />
          </div>
        </div>

        {isPublicTest && (
          <button
            type="button"
            onClick={() => setShowPreviewTools((value) => !value)}
            aria-expanded={showPreviewTools}
            aria-controls="monitor-preview-tools"
            className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-black text-slate-800 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-400/25 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
          >
            <span className="inline-flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" />{tx({ zh: '手动预览控制', en: 'Manual preview controls' })}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showPreviewTools ? 'rotate-180' : ''}`} />
          </button>
        )}

        {toolsVisible && (
          <div id="monitor-preview-tools" className="space-y-5" data-testid="monitor-preview-tools">
            <section>
              <div className="mb-2.5 flex items-center justify-between gap-2">
                <h3 className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                  <Sparkles className="h-4 w-4" />
                  {tx({ zh: '眼睛主题', en: 'Eye theme' })}
                </h3>
                <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[10px] font-black text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">{eyeTheme}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3" role="group" aria-label={tx({ zh: '眼睛主题', en: 'Eye theme' })}>
                {THEMES.map((theme) => (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => setEyeTheme(theme.id)}
                    aria-pressed={eyeTheme === theme.id}
                    className={`rounded-xl border px-3 py-2.5 text-xs font-bold transition focus:outline-none focus:ring-2 focus:ring-indigo-400/30 ${eyeTheme === theme.id ? 'border-indigo-600 bg-indigo-600 text-white shadow-md shadow-indigo-600/15' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'}`}
                    title={tx({ zh: theme.titleZh || theme.zh, en: theme.titleEn || theme.en })}
                  >
                    {tx({ zh: theme.zh, en: theme.en })}
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-[22px] bg-slate-50 p-4 dark:bg-slate-900/60">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                  <Palette className="h-4 w-4" />
                  {tx({ zh: '眼睛颜色', en: 'Eye color' })}
                </h3>
                <span className="font-mono text-[11px] font-bold text-slate-500 dark:text-slate-400">{eyeColor}</span>
              </div>
              <div className="flex flex-wrap gap-2.5" role="group" aria-label={tx({ zh: '眼睛颜色', en: 'Eye color' })}>
                {EYE_COLORS.map((item) => (
                  <button
                    key={item.color}
                    type="button"
                    aria-pressed={eyeColor === item.color}
                    onClick={() => {
                      setEyeColor(item.color)
                      lastSentEyeColorRef.current = item.color
                      sendEyeColor(item.color, { sendToDevice: controlReady, silent: false })
                    }}
                    className={`h-9 w-9 rounded-full border-4 transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 ${eyeColor === item.color ? 'border-indigo-500 shadow-md' : 'border-white shadow-sm dark:border-slate-800'}`}
                    title={item.name}
                    aria-label={`${tx({ zh: '眼睛颜色', en: 'Eye color' })}: ${item.name}`}
                    style={{ backgroundColor: item.color }}
                  />
                ))}
              </div>
              <p className="mt-3 text-[11px] font-medium leading-relaxed text-slate-400">
                {controlReady
                  ? tx({ zh: '颜色会同步发送到在线设备。', en: 'Color changes are sent to the online device.' })
                  : tx({ zh: '设备离线：颜色只影响当前预览。', en: 'Device offline: color only affects this preview.' })}
              </p>
            </section>

            <section>
              <div className="mb-2.5 flex items-center justify-between gap-2">
                <h3 className="text-xs font-black uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">{tx({ zh: '表情控制', en: 'Emotion control' })}</h3>
                <span className="text-[11px] font-semibold text-slate-400">{controlReady ? tx({ zh: '同步设备', en: 'Device sync' }) : tx({ zh: '仅预览', en: 'Preview only' })}</span>
              </div>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 xl:grid-cols-4 2xl:grid-cols-6" role="group" aria-label={tx({ zh: '表情控制', en: 'Emotion control' })}>
                {EMOTION_PRESETS.map((preset) => (
                  <button
                    key={preset.emotion}
                    type="button"
                    onClick={() => sendEmotion(preset, { sendToDevice: controlReady })}
                    aria-pressed={emotion === preset.emotion}
                    className={`group flex min-w-0 flex-col items-center gap-1 rounded-2xl border px-1.5 py-2.5 transition focus:outline-none focus:ring-2 focus:ring-indigo-400/30 ${emotion === preset.emotion ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'}`}
                    title={tx(emotionLabelEntry(preset.emotion))}
                  >
                    <span className="text-xl transition group-hover:scale-110">{preset.emoji}</span>
                    <span className="w-full truncate text-[9px] font-bold">{tx(emotionLabelEntry(preset.emotion))}</span>
                  </button>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </aside>
  )
}
